/** Helpers shared by widgets: the visible task set for a filter, as plain Task objects. */
import type { LoroDoc } from "loro-crdt";
import { computeAll } from "../core/computed";
import { dayIn, matches, type FilterAst, type FilterCtx } from "../core/filter";
import { snapshotOf } from "../core/ops";
import { Snapshot, groupsOf, sequencesOf } from "../core/structure";
import type { Task } from "../core/schema";

export interface Visible {
  snapshot: Snapshot;
  tasks: Task[];
}

export function filterCtx(s: Snapshot, nowMs: number): FilterCtx {
  return {
    computed: (t, key) => (computeAll(s, t.id, nowMs) as Record<string, unknown>)[key],
    groupsOf: (id) => groupsOf(s, id),
    seqsOf: (id) => sequencesOf(s, id),
    parentOf: (t) => s.parentOf(t.id),
    // date comparisons (ADR 0020) count from the device's day at `nowMs`
    today: dayIn(nowMs),
  };
}

export function toTask(id: string, rec: Record<string, unknown>): Task {
  return {
    ...(rec as unknown as Task),
    id,
    props: ((rec.props as Task["props"]) ?? {}) as Task["props"],
    title: String(rec.title ?? ""),
    notes: String(rec.notes ?? ""),
  };
}

/** Live (not archived) tasks matching the filter, oldest first. Notes are excluded unless `includeNotes`. */
export function visibleTasks(
  doc: LoroDoc,
  filter: FilterAst | undefined,
  nowMs: number,
  opts: { includeNotes?: boolean; includeDone?: boolean; includeSomeday?: boolean } = {},
): Visible {
  const snapshot = snapshotOf(doc);
  const ctx = filterCtx(snapshot, nowMs);
  const tasks: Task[] = [];
  for (const [id, rec] of Object.entries(snapshot.tasks)) {
    const t = toTask(id, rec);
    if (t.archivedAt) continue;
    if (!opts.includeNotes && t.typeId === "note") continue;
    if (!opts.includeDone && t.doneAt) continue;
    if (!opts.includeSomeday && t.props.horizon === "someday") continue;
    if (!matches(t, filter, ctx)) continue;
    tasks.push(t);
  }
  // a fixed order: the doc's map order can change with any update (one from another device too), and a widget
  // that showed it would move cards under the pointer (§9.19); oldest first, like the list, then by id
  tasks.sort(byCreated);
  return { snapshot, tasks };
}

/** Oldest first; tasks made in the same millisecond keep a fixed order by id. */
export const byCreated = (a: Pick<Task, "createdAt" | "id">, b: Pick<Task, "createdAt" | "id">) =>
  a.createdAt.localeCompare(b.createdAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const FILTER_SCHEMA = {
  type: "object",
  title: "config.filter",
  description: "config.filterHint",
  "x-drang": "filter",
} as const;
